// =============================================================================
// ORDER REQUEST CREDIT VALIDATION
// =============================================================================
// This script validates that customers have sufficient credits before
// allowing them to submit an order request.
//
// To enable debug logging, uncomment the line below:
// const DEBUG = true;
const DEBUG = false;

// Helper for conditional logging
const log = (...args) => DEBUG && console.log(...args);

// Cache the last validated cart data to avoid redundant fetches
let lastValidatedCart = null;

// Track current warning content to prevent unnecessary DOM updates (stops blinking)
let currentWarningHTML = '';

/**
 * Updates warning element only if content changed (prevents blinking)
 * @param {HTMLElement} warningEl - Warning element
 * @param {string} newHTML - New HTML content
 */
function updateWarningIfChanged(warningEl, newHTML) {
  if (currentWarningHTML !== newHTML) {
    log("[DEBUG] Warning content changed, updating DOM");
    warningEl.innerHTML = newHTML;
    currentWarningHTML = newHTML;
  } else {
    log("[DEBUG] Warning content unchanged, skipping DOM update");
  }
}

/**
 * Extracts full cart items from DOM (FAST - no API call!)
 * @returns {Array} Array of cart items with {title, qty, credits, image}
 */
function parseCartItemsFromDOM() {
  log("[DEBUG] Parsing cart items from DOM...");

  try {
    const items = [];

    // Find all cart item rows
    const rows = document.querySelectorAll('tr[ref="cartItemRows[]"]');

    rows.forEach((row, index) => {
      // Extract product title
      const titleEl = row.querySelector('.cart-items__title');
      const title = titleEl?.textContent.trim() || 'Unknown Product';

      // Extract quantity
      const qtyInput = row.querySelector('input[data-cart-quantity]');
      const qty = parseInt(qtyInput?.value || '1', 10);

      // Extract line price from data attribute (no parsing needed!)
      const priceEl = row.querySelector('.cart-items__price text-component');
      const credits = parseFloat(priceEl?.dataset.credits || '0');

      // Extract image
      const imgEl = row.querySelector('.cart-items__media-image');
      const image = imgEl?.src || null;

      // Extract line number (1-indexed) and key for cart removal
      const line = index + 1;
      const key = row.dataset.key || null;

      // Extract SKU from data attribute
      const sku = row.dataset.sku || null;

      // Extract coordinates from metafield
      const coordinates = row.dataset.coordinates || null;

      items.push({ title, qty, credits, image, line, key, sku, coordinates });
    });

    log(`[DEBUG] Parsed ${items.length} items from DOM:`, items);

    if (items.length === 0) {
      console.warn("Order Request: No cart items parsed from DOM");
      return null;
    }

    return items;
  } catch (e) {
    console.error("Order Request: Error parsing cart items from DOM:", e);
    return null;
  }
}

/**
 * Validates credit balance by reading DOM (FAST - no fetch!)
 * @returns {Promise<{valid: boolean, total: number, items: Array}>}
 */
async function validateCreditsFromDOM() {
  log("[DEBUG] validateCreditsFromDOM called");

  const btn = document.getElementById("request-order-btn");
  const warningEl = document.getElementById("wallet-warning");

  log("[DEBUG] Button element:", btn);
  log("[DEBUG] Warning element:", warningEl);

  if (!btn || !warningEl) {
    log("[DEBUG] Required elements not found!");
    return { valid: false, total: 0, items: [] };
  }

  try {
    log("[DEBUG] Reading cart from DOM...");

    // Read cart total from DOM (no fetch needed!)
    const cartTotalEl = document.querySelector('[ref="cartTotal"]');
    const cartItemCountEl = document.querySelector('[ref="cartItemCount"]');

    log("[DEBUG] Cart total element:", cartTotalEl);
    log("[DEBUG] Cart item count element:", cartItemCountEl);

    if (!cartTotalEl) {
      log("[DEBUG] Cart total element not found, falling back to API fetch");
      return await validateCreditsFromAPI();
    }

    // Read raw numeric value from data attribute (no parsing needed!)
    const total = parseFloat(cartTotalEl.dataset.credits || '0');
    const itemCount = parseInt(cartItemCountEl?.textContent || '0', 10);

    log(`[DEBUG] Cart total from DOM: ${total} credits, ${itemCount} items`);

    // Empty cart check
    if (itemCount === 0) {
      updateWarningIfChanged(warningEl, "");
      btn.disabled = true;
      btn.style.opacity = "0.5";
      btn.style.cursor = "not-allowed";
      return { valid: false, total: 0, items: [] };
    }

    const creditsAvailable = window.customerCredits || 0;

    log(`[DEBUG] Comparing: ${total} (needed) vs ${creditsAvailable} (available)`);

    // Not enough credits → show warning with link, disable button
    if (total > creditsAvailable) {
      log("[DEBUG] INSUFFICIENT credits - disabling button");
      const shortage = total - creditsAvailable;
      const warningHTML = `
        <div style="text-align: left;">
          Недостатъчно жълтици за тази заявка<br>
          <span style="font-size: 0.9em; opacity: 0.9;">Нужни: <strong>${total}</strong> | Налични: <strong>${creditsAvailable}</strong> | Липсват: <strong>${shortage}</strong></span><br>
          <a href="/pages/how-it-works" style="color: #b30000; text-decoration: underline; font-weight: 600; font-size: 0.95em; margin-top: 4px; display: inline-block;">Научете как да вземете жълтици →</a>
        </div>
      `.trim();

      updateWarningIfChanged(warningEl, warningHTML);

      btn.disabled = true;
      btn.style.opacity = "0.5";
      btn.style.cursor = "not-allowed";

      // Don't fetch items - not needed if validation failed
      const result = { valid: false, total, items: [] };
      lastValidatedCart = result;
      log("[DEBUG] Cached validation result:", result);
      return result;
    }

    // Enough credits → clear warning, enable button
    log("[DEBUG] SUFFICIENT credits - enabling button");
    updateWarningIfChanged(warningEl, "");
    btn.disabled = false;
    btn.style.opacity = "1";
    btn.style.cursor = "pointer";

    // Parse items from DOM immediately (no fetch needed!)
    const items = parseCartItemsFromDOM();

    log("[DEBUG] Button enabled with items cached");
    const result = { valid: true, total, items: items || [] }; // items from DOM!
    lastValidatedCart = result;
    log("[DEBUG] Cached validation result:", result);
    return result;
  } catch (e) {
    console.error("Error validating credits from DOM:", e);
    return await validateCreditsFromAPI();
  }
}

/**
 * Fallback: Validates credit balance using /cart.js API (SLOW - 13 seconds!)
 * @returns {Promise<{valid: boolean, total: number, items: Array}>}
 */
async function validateCreditsFromAPI() {
  const btn = document.getElementById("request-order-btn");
  const warningEl = document.getElementById("wallet-warning");

  if (!btn || !warningEl) return { valid: false, total: 0, items: [] };

  try {
    log("[DEBUG] Fetching cart via API (this may be slow)...");
    const fetchStart = performance.now();
    const cart = await fetch("/cart.js").then((r) => r.json());
    log(`[DEBUG] API fetch took ${(performance.now() - fetchStart).toFixed(0)}ms`);

    // Empty cart check
    if (cart.item_count === 0) {
      updateWarningIfChanged(warningEl, "");
      btn.disabled = true;
      btn.style.opacity = "0.5";
      btn.style.cursor = "not-allowed";
      return { valid: false, total: 0, items: [] };
    }

    let total = 0;
    const items = cart.items.map((item) => {
      const credits = item.final_line_price / 100;
      total += credits;

      return {
        title: item.product_title,
        qty: item.quantity,
        credits: credits,
        image: item.image,
        sku: item.sku || null,
        coordinates: null  // Note: Coordinates not available via API fallback
      };
    });

    const creditsAvailable = window.customerCredits || 0;

    // Not enough credits → show warning with link, disable button
    if (total > creditsAvailable) {
      const shortage = total - creditsAvailable;
      const warningHTML = `
        <div style="text-align: left;">
          Недостатъчно жълтици за тази заявка<br>
          <span style="font-size: 0.9em; opacity: 0.9;">Нужни: <strong>${total}</strong> | Налични: <strong>${creditsAvailable}</strong> | Липсват: <strong>${shortage}</strong></span><br>
          <a href="/pages/how-it-works" style="color: #b30000; text-decoration: underline; font-weight: 600; font-size: 0.95em; margin-top: 4px; display: inline-block;">Научете как да вземете жълтици →</a>
        </div>
      `.trim();

      updateWarningIfChanged(warningEl, warningHTML);

      btn.disabled = true;
      btn.style.opacity = "0.5";
      btn.style.cursor = "not-allowed";

      return { valid: false, total, items };
    }

    // Enough credits → clear warning, enable button
    updateWarningIfChanged(warningEl, "");
    btn.disabled = false;
    btn.style.opacity = "1";
    btn.style.cursor = "pointer";

    const result = { valid: true, total, items };
    lastValidatedCart = result;
    return result;
  } catch (e) {
    console.error("Error validating credits via API:", e);
    const warningEl = document.getElementById("wallet-warning");
    if (warningEl) {
      warningEl.textContent = "Възникна грешка. Моля, опитайте отново.";
      warningEl.style.color = "#b30000";
      warningEl.style.fontWeight = "600";
    }

    return { valid: false, total: 0, items: [] };
  }
}

// Listen for ALL possible cart update events
const cartEvents = [
  'theme:cart-update',
  'cart:update',
  'cart-update',
  'theme:cart:update'
];

cartEvents.forEach(eventName => {
  document.addEventListener(eventName, () => {
    log(`[DEBUG] Cart event received: ${eventName}`);
    // Invalidate cache when cart changes
    lastValidatedCart = null;
    // Small delay to let DOM update from morph
    setTimeout(() => {
      log("[DEBUG] Running validation after cart event...");
      validateCreditsFromDOM();
    }, 100);
  });
});

// Also validate whenever cart content changes via MutationObserver
let mutationTimeout = null;
const observeCartChanges = () => {
  const cartElement = document.querySelector('cart-items-component');
  if (!cartElement) {
    log("[DEBUG] Cart element not found for observation");
    return;
  }

  log("[DEBUG] Setting up MutationObserver on cart");
  const observer = new MutationObserver(() => {
    // Debounce mutations - wait for DOM to settle
    if (mutationTimeout) clearTimeout(mutationTimeout);

    mutationTimeout = setTimeout(() => {
      log("[DEBUG] Cart DOM mutation detected, invalidating cache");
      lastValidatedCart = null;
      validateCreditsFromDOM();
    }, 300);
  });

  observer.observe(cartElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-cart-quantity']
  });
};

// Validate on page load
document.addEventListener("DOMContentLoaded", () => {
  log("[DEBUG] DOMContentLoaded - running initial validation");
  validateCreditsFromDOM();
  observeCartChanges();
});

// Handle button click
document.addEventListener("click", async (event) => {
  // Find our button even if click is on a child element and even if inside shadow DOM
  let btn = null;

  if (event.composedPath) {
    const path = event.composedPath();
    for (const el of path) {
      if (el instanceof HTMLElement && el.id === "request-order-btn") {
        btn = el;
        break;
      }
    }
  } else {
    btn = event.target.closest("#request-order-btn");
  }

  if (!btn) return; // Click is not for us

  const warningEl = document.getElementById("wallet-warning");

  // Avoid double-click spam
  if (btn.dataset.loading === "1") return;
  btn.dataset.loading = "1";

  try {
    log("[DEBUG] Button clicked");
    log("[DEBUG] Button disabled?", btn.disabled);
    log("[DEBUG] Has cache?", !!lastValidatedCart);
    log("[DEBUG] Cache data:", lastValidatedCart);
    const startTime = performance.now();

    // ALWAYS re-validate from DOM on click to ensure we have fresh data
    // This protects against stale cache if events didn't fire
    log("[DEBUG] Re-validating from DOM before proceeding...");
    await validateCreditsFromDOM();

    if (!lastValidatedCart || btn.disabled) {
      log("[DEBUG] Validation failed or button disabled, cannot proceed");
      btn.dataset.loading = "";
      return;
    }

    const { valid, total, items } = lastValidatedCart;

    if (!valid) {
      log("[DEBUG] Validation failed (insufficient credits)");
      btn.dataset.loading = "";
      return;
    }

    log("[DEBUG] Validation passed, proceeding with order...");

    // Items are ALREADY in cache from DOM parsing - no fetch needed!
    log("[DEBUG] Using cached items from DOM:", items);

    // Validate we have items
    if (!items || items.length === 0) {
      console.error("[DEBUG] No items in cache! This should not happen.");
      btn.textContent = "Изпратете заявка";
      if (warningEl) {
        warningEl.textContent = "Грешка при четене на кошницата. Моля, опитайте отново.";
        warningEl.style.color = "#b30000";
      }
      btn.dataset.loading = "";
      return;
    }

    log("[DEBUG] Building payload...");
    const payload = {
      email: window.customerEmail || "",
      credits_available: window.customerCredits || 0,
      credits_needed: total,
      items, // Already parsed from DOM - instant!
      timestamp: new Date().toISOString()
    };

    log("[DEBUG] Storing in sessionStorage...");
    sessionStorage.setItem('orderRequestData', JSON.stringify(payload));

    log(`[DEBUG] Total time: ${(performance.now() - startTime).toFixed(0)}ms`);
    log("[DEBUG] Redirecting...");

    // Redirect to order request page
    window.location.href = `/pages/order-request`;
  } catch (e) {
    console.error("Error building order request:", e);
    btn.textContent = "Изпратете заявка";
    if (warningEl) {
      warningEl.textContent = "Възникна грешка. Моля, опитайте отново.";
      warningEl.style.color = "#b30000";
      warningEl.style.fontWeight = "600";
    }
  } finally {
    btn.dataset.loading = "";
  }
});
